import React, { useEffect, useState, lazy } from 'react';
export function Reason1() {
  const [activeRegion, setActiveRegion] = useState<string>('frontal');
  const [activePhase, setActivePhase] = useState<number>(1);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const regions = {
    frontal: {
      title: 'Frontal Region',
      symptom: 'Forehead pressure & pain',
      misconception: 'Frontal sinus infection',
      reality: 'Trigeminal nerve (V1) irritation',
      percentage: 78
    },
    maxillary: {
      title: 'Maxillary Region',
      symptom: 'Cheek & upper teeth pain',
      misconception: 'Maxillary sinus infection',
      reality: 'Trigeminal nerve (V2) irritation',
      percentage: 64
    },
    orbital: {
      title: 'Orbital Region',
      symptom: 'Behind-eye pressure',
      misconception: 'Eye strain or infection',
      reality: 'Ophthalmic nerve compression',
      percentage: 82
    },
    temporal: {
      title: 'Temporal Region',
      symptom: 'Temple throbbing',
      misconception: 'Tension headache',
      reality: 'Temporal artery inflammation',
      percentage: 71
    }
  };
  useEffect(() => {
    // Set up intersection observer for this component
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setIsIntersecting(true);
      }
    }, {
      threshold: 0.1
    });
    const element = document.getElementById('reason-1');
    if (element) {
      observer.observe(element);
    }
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);
  return <div className="py-12 border-t border-[#F1F5F9]" data-id="element-210">
      <div className="flex flex-col md:flex-row md:gap-8" data-id="element-211">
        <div className="md:w-2/3" data-id="element-212">
          <div className="flex items-center mb-4" data-id="element-213">
            <div className="bg-[#0EA5B7] text-white font-bold rounded-full w-10 h-10 flex items-center justify-center mr-4" data-id="element-214">
              1
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-215">Reason #1:</h2>
          </div>
          <h3 className="text-xl font-semibold text-[#0F172A] mb-6" data-id="element-216">
            The Truth They Never Told You (And Why Your "Sinus Headaches" Are
            Actually Something Else)
          </h3>
          <div className="mb-8" data-id="element-217">
            <picture data-id="element-218">
              <source srcSet="https://uploadthingy.s3.us-west-1.amazonaws.com/2DTcTkJAShCbGK6DgVEDRs/Gemini_Generated_Image_v2n96sv2n96sv2n9_%281%29_%281%29.webp" type="image/webp" data-id="element-219" />
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2DTcTkJAShCbGK6DgVEDRs/Gemini_Generated_Image_v2n96sv2n96sv2n9_%281%29_%281%29.webp" alt="Doctor explaining trigeminal nerve and migraine connection" className="w-full rounded-lg shadow-md" width="800" height="450" loading="lazy" decoding="async" style={{
              contentVisibility: 'auto'
            }} data-id="element-220" />
            </picture>
          </div>
          <div className="prose prose-slate max-w-none" data-id="element-221">
            <p data-id="element-222">
              You know that crushing pressure behind your eyes? The one that
              makes you want to drill a hole in your skull just to release it?
            </p>
            <p data-id="element-223">
              Your doctor probably called it a sinus headache. Prescribed some
              decongestants. Maybe antibiotics.
            </p>
            <p data-id="element-224">
              Here's what they didn't tell you:{' '}
              <strong data-id="element-225">
                90% of "sinus headaches" are actually migraines in disguise.
              </strong>
            </p>
            <p data-id="element-226">
              I discovered this after finding a buried Johns Hopkins study that
              revealed the trigeminovascular system — your brain's largest
              cranial nerve network — creates identical symptoms to sinus
              pressure when it's activated. The same nerve branches (V1 and V2
              divisions, if you want to get technical) that control sinus
              sensation also trigger migraine pain.
            </p>
            <p data-id="element-227">But here's where it gets interesting...</p>
            <p data-id="element-228">This isn't random. It's a precise 4-phase chain reaction:</p>
            <ol className="list-decimal pl-6 space-y-2 my-4" data-id="element-229">
              <li data-id="element-230">
                <strong data-id="element-231">Phase 1:</strong> Muscle tension in your neck,
                shoulders, and around your eyes restricts blood flow. (That's
                why you unconsciously rub your temples.)
              </li>
              <li data-id="element-232">
                <strong data-id="element-233">Phase 2:</strong> Your blood vessels compensate by
                dilating, becoming inflamed. This releases CGRP and substance P
                — inflammatory chemicals that light up your trigeminal nerve
                like a Christmas tree.
              </li>
              <li data-id="element-234">
                <strong data-id="element-235">Phase 3:</strong> Your nerve endings become
                hypersensitive. Normal sensations become unbearable. Light feels
                like needles. Sound becomes torture.
              </li>
              <li data-id="element-236">
                <strong data-id="element-237">Phase 4:</strong> The pain creates more tension, which
                restricts more blood flow, which triggers more inflammation...
                and the cycle feeds itself.
              </li>
            </ol>
            <div className="bg-[#F1F5F9] p-4 rounded-lg border-l-4 border-[#0EA5B7] my-6" data-id="element-238">
              <p className="italic" data-id="element-239">
                One Reddit user described it perfectly: "It feels like a severe
                toothache and brain freeze at the same time that doesn't go
                away."
              </p>
            </div>
            <div className="mb-6" data-id="element-240">
              {isIntersecting && <picture data-id="element-241">
                  <source srcSet="https://uploadthingy.s3.us-west-1.amazonaws.com/cvQrAhzfDC3yzxG5tUCQE6/imgi_3_ChatGPT_Image_Sep_92C_20252C_12_35_15_AM.webp" type="image/webp" data-id="element-242" />
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cvQrAhzfDC3yzxG5tUCQE6/imgi_3_ChatGPT_Image_Sep_92C_20252C_12_35_15_AM.webp" alt="90% of Sinus Headaches Are Actually Migraines" className="w-full rounded-lg shadow-md" width="800" height="450" loading="lazy" decoding="async" style={{
                contentVisibility: 'auto'
              }} data-id="element-243" />
                </picture>}
            </div>
            <p data-id="element-244">
              That's not a headache. That's your trigeminal nerve caught in an
              endless loop of its own making.
            </p>
            <p data-id="element-245">
              The iRelief Oculis-3 works because it interrupts all four phases
              simultaneously — something no pill, injection, or single-mode
              treatment can do. But I'm getting ahead of myself...
            </p>
          </div>
        </div>
        <div className="md:w-1/3 mt-8 md:mt-0" data-id="element-246">
          <div className="bg-white rounded-lg shadow-sm border border-[#F1F5F9] p-4" data-id="element-247">
            <h4 className="font-medium text-[#0F172A] mb-4" data-id="element-248">
              Migraine vs. Sinus: The Misdiagnosis
            </h4>
            <div className="bg-[#F1F5F9] p-4 rounded-lg mb-4" data-id="element-249">
              <h5 className="font-medium text-[#0F172A] mb-2" data-id="element-250">
                {regions[activeRegion].title}
              </h5>
              <div className="space-y-3 text-sm" data-id="element-251">
                <div className="flex justify-between" data-id="element-252">
                  <span className="text-[#334155]" data-id="element-253">Common symptom:</span>
                  <span className="font-medium text-[#0F172A]" data-id="element-254">
                    {regions[activeRegion].symptom}
                  </span>
                </div>
                <div className="flex items-center space-x-2" data-id="element-255">
                  <div className="h-px bg-[#E2E8F0] flex-grow" data-id="element-256"></div>
                  <span className="text-xs text-[#64748B]" data-id="element-257">VS</span>
                  <div className="h-px bg-[#E2E8F0] flex-grow" data-id="element-258"></div>
                </div>
                <div className="p-3 bg-white rounded-md border border-red-200" data-id="element-259">
                  <div className="text-xs text-red-500 uppercase font-semibold mb-1" data-id="element-260">
                    What doctors often diagnose:
                  </div>
                  <div className="text-[#334155]" data-id="element-261">
                    {regions[activeRegion].misconception}
                  </div>
                </div>
                <div className="p-3 bg-white rounded-md border border-[#0EA5B7] border-opacity-40" data-id="element-262">
                  <div className="text-xs text-[#0EA5B7] uppercase font-semibold mb-1" data-id="element-263">
                    What's actually happening:
                  </div>
                  <div className="text-[#334155]" data-id="element-264">
                    {regions[activeRegion].reality}
                  </div>
                </div>
                <div className="pt-2 mt-2 border-t border-[#E2E8F0] flex justify-between items-center" data-id="element-265">
                  <span className="text-xs text-[#334155]" data-id="element-266">
                    Misdiagnosis rate:
                  </span>
                  <div className="flex items-center" data-id="element-267">
                    <div className="w-24 h-2 bg-[#F1F5F9] rounded-full mr-2 overflow-hidden" data-id="element-268">
                      <div className="h-full bg-[#0EA5B7]" style={{
                      width: `${regions[activeRegion].percentage}%`,
                      willChange: 'width'
                    }} data-id="element-269"></div>
                    </div>
                    <span className="text-sm font-medium text-[#0EA5B7]" data-id="element-270">
                      {regions[activeRegion].percentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default {
  Reason1
};